import React, {useEffect, useState} from 'react';
import {PageTitle} from "../../../components/page-title";
import "./index.css";
import {Steps} from "antd";
import BottomPageConfirmation from "../../../components/BottomPageConfirmation";
import {Routes} from "../../../common/Routes";
import {useHistory, useLocation} from "react-router-dom";
import {NotFound} from "../../../components/notFound";
import { CreatePolicyActions, EditPolicyActions, manageType, PolicyInputRequirementsActions } from './model';
import PolicyInput from './policyInput';
import PolicyPreview from "./policyPreview";
import {useDispatch} from "react-redux";
import PolicyManageResult from "./policyManageResult";
import {ManagePolicyController} from "./controller";
import {ApiCallStatusV2} from "../../../common/ApiCall";

const {Step} = Steps;

interface ManagePolicyComponentProps {
  pageTitle?: string;
  steps?: {
    title: string,
    content: JSX.Element
  }[];
  confirmationText?: string[];
  buttonText?: string[];
  cancelText?: string[];
  isButtonDisabled?: boolean;
  nextStep?: () => void;
  cancelStep?: () => void;
}

const ManagePolicy = ({ match }) => {
  const history = useHistory();
  const location = useLocation();
  const pathname = location.pathname;

  const [current, setCurrent] = useState<number>(0);
  const [policyName, setPolicyName] = useState<string>("");
  const [policyId, setPolicyId] = useState<string>("");
  const [policyNameError, setPolicyNameError] = useState<string>("");
  const [scopeInfo, setScopeInfo] = useState<string>("");
  const [group, setGroup] = useState<string>("");
  const [systemResource, setSystemResource] = useState<string[]>([]);
  const [scope, setScope] = useState<string | undefined>(undefined);
  const [scopeOptions, setScopeOptions] = useState<string[]>([]);
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true);
  const [scopeAndActionsVisible, setScopeAndActionsVisible] = useState<boolean>(false);
  const [actionType, setActionType] = useState<string>("");

  const dispatch = useDispatch();

  const {
    createPolicyPEV,
    policy,
    policyInputRequirementsPEV,
    userGroups
  } = ManagePolicyController();

  const createPolicyApiCallStatus = new ApiCallStatusV2(createPolicyPEV.progress, createPolicyPEV.error)
  const policyRequirementApiCallStatus = new ApiCallStatusV2(policyInputRequirementsPEV.progress, policyInputRequirementsPEV.error)


  useEffect(() => {
    if (!policyRequirementApiCallStatus.isCalled){
      if (match.params.id !== undefined) {
        dispatch(PolicyInputRequirementsActions.getPolicyInputRequirements(match.params.id));        
      } else {
        dispatch(PolicyInputRequirementsActions.getPolicyInputRequirements());
      }
    }
    return ()=>{
      dispatch(PolicyInputRequirementsActions.resetPolicyInputRequirements())
    }
  }, [match.params.id]);


  useEffect(() => {
    if (policyRequirementApiCallStatus.isCompletedSuccessfully && match.params.id !== undefined){
      setPolicyName(policy.name);
      setGroup(policy.group.name);
      setScope(policy.scope);
      setSystemResource(policy.systemResource.map(systemResource => systemResource.systemResourceId))
      setPolicyId(policy.id);
    }
  }, [policyRequirementApiCallStatus.isCompletedSuccessfully]);

  const nextStep = () => {
    if (current === 0) {
      setCurrent(current + 1);
    } else if (current === 1) {
      const filteredData = userGroups.filter(item => item.name === group);
      const inputData = {
        name: policyName,
        group: {
          type: "UserGroup",
          typeId: filteredData[0].type,
          id: filteredData[0].id
        },
        systemResources: systemResource,
        scope: {name: scope},
        actionNames: ["can_view_dashboards"],
        policyId: policyId
      }
      if(actionType === manageType.Create)
        dispatch(CreatePolicyActions.createPolicy(inputData));
      else if(actionType === manageType.Edit)
        dispatch(EditPolicyActions.editPolicy(inputData));
      setCurrent(current + 1);
    } else {
      history.push(`${Routes.Policies.path}`);
      setCurrent(0);
    }
  };

  const cancelStep = () => {
    if (current === 0) {
      history.push(`${Routes.Policies.path}`);
      setCurrent(0);
    } else if (current === 1) {
      setCurrent(current - 1);
    } else {
      setCurrent(current - 1);
    }
  };

  const ManagePolicyComponent = ({actionType}) => {
    setActionType(actionType);
    const props: ManagePolicyComponentProps = {};
    if (actionType === manageType.Create) {
      props.pageTitle = "Create Policy"
      props.steps = [{
        title: "Create Policy",
        content: <PolicyInput
          policyName={policyName} setPolicyName={setPolicyName}
          group={group} setGroup={setGroup}
          systemResource={systemResource} setSystemResource={setSystemResource}
          scope={scope} setScope={setScope}
          scopeOptions={scopeOptions} setScopeOptions={setScopeOptions}
          policyNameError={policyNameError} setPolicyNameError={setPolicyNameError}
          scopeAndActionsVisible={scopeAndActionsVisible} setScopeAndActionsVisible={setScopeAndActionsVisible}
          scopeInfo={scopeInfo} setScopeInfo={setScopeInfo}
                 />
      }, {
        title: "Preview",
        content: <PolicyPreview
          policyName={policyName} group={group}
          systemResource={systemResource} scope={scope}
                 />
      }, {
        title: "Submit",
        content: <PolicyManageResult
          policyName={policyName} setCurrent={setCurrent}
                 />
      }]
      props.confirmationText = ["Please enter the required parameters for your policy. Ensure all fields are filled out accurately to proceed.",
        "Review the parameters you have entered. Click 'Submit' to finalize your policy based on the parameters you provided.",
        "Policy creation failed. Please check the parameters you entered to ensure all information is correct, and then try again."]
      props.buttonText = ["Preview & Save", "Submit", "Back to policy list page"]
      props.cancelText = ["Cancel", "Back", "Back to previous step"]
      if (policyName !== "" && group !== "" && systemResource.length !== 0 && scope !== undefined) {
        setIsButtonDisabled(false);
      } else {
        setIsButtonDisabled(true);
      }
    } else if (actionType === manageType.Edit) {
      props.pageTitle = "Edit Policy"
      props.steps = [{
        title: "Edit Policy",
        content: <PolicyInput
                    policyName={policyName} setPolicyName={setPolicyName}
                    group={group} setGroup={setGroup}
                    systemResource={systemResource} setSystemResource={setSystemResource}
                    scope={scope} setScope={setScope}
                    scopeOptions={scopeOptions} setScopeOptions={setScopeOptions}
                    policyNameError={policyNameError} setPolicyNameError={setPolicyNameError}
                    scopeAndActionsVisible={systemResource.length!==0?true:false} setScopeAndActionsVisible={setScopeAndActionsVisible}
                    scopeInfo={scopeInfo} setScopeInfo={setScopeInfo}
                 />
      }, {
        title: "Preview",
        content: <PolicyPreview
          policyName={policyName} group={group}
          systemResource={systemResource} scope={scope}
                 />
      }, {
        title: "Submit",
        content: <PolicyManageResult
        policyName={policyName} setCurrent={setCurrent}
                 />
      }]
      props.buttonText = ["Preview & Save", "Submit", "Back to policy list page"]
      props.cancelText = ["Cancel", "Back", "Back to previous step"]
      props.confirmationText = ["Please enter the required parameters for your policy. Ensure all fields are filled out accurately to proceed.",
        "Review the parameters you have entered. Click 'Submit' to finalize your policy based on the parameters you provided.",
        "Policy creation failed. Please check the parameters you entered to ensure all information is correct, and then try again."]
        if (policyName !== "" && group !== "" && systemResource.length !== 0 && scope !== undefined) {
          setIsButtonDisabled(false);
        } else {
          setIsButtonDisabled(true);
        }
    } else if (actionType === manageType.Delete) {
      //todo Add delete policy props
      props.pageTitle = "Delete Policy"
      props.steps = [{
        title: "Delete Policy",
        content: <div>Step 1 Content</div>
      }, {
        title: "Preview",
        content: <div>Step 2 Content</div>
      }, {
        title: "Submit",
        content: <div>Step 3 Content</div>
      }]
      props.buttonText = ["Preview & Save", "Submit", "Back to policy list page"]
      props.cancelText = ["Cancel", "Back", "Back to previous step"]
      props.confirmationText = ["Please enter the required parameters for your policy. Ensure all fields are filled out accurately to proceed.",
        "Review the parameters you have entered. Click 'Submit' to finalize your policy based on the parameters you provided.",
        "Policy creation failed. Please check the parameters you entered to ensure all information is correct, and then try again."]
    } else {
      return null;
    }
    return (
      <div className={"managePolicyPageContainer"}>
        <div className={"managePolicyContainer"}>
          <PageTitle title={props.pageTitle}/>
          <Steps current={current} labelPlacement={"vertical"} size={"small"}>
            {props.steps.map((step, index) => (
              <Step key={index} title={step.title}/>
            ))}
          </Steps>
          <div className="steps-content">{props.steps[current].content}</div>
        </div>
        {!createPolicyApiCallStatus.isFailed && createPolicyPEV.value !== null ?
          null
          : <div className={"confirmation"}>
            <BottomPageConfirmation
              confirmationText={props.confirmationText[current]} buttonText={props.buttonText[current]}
              cancelText={props.cancelText[current]}
              isButtonDisabled={isButtonDisabled} onClick={nextStep} onClickCancel={cancelStep}
            />
            </div>
        }
      </div>
    );
  }

  const renderComponent = () => {
    const actionMap = {
      [manageType.Create]: manageType.Create,
      [manageType.Edit]: manageType.Edit,
      [manageType.Delete]: manageType.Delete,
    };

    const actionType = Object.keys(actionMap).find(type => pathname.includes(type));

    if (actionType) {
      return <ManagePolicyComponent actionType={actionMap[actionType]}/>;
    } else {
      return <NotFound/>;
    }
  };

  return (
    <>
      {renderComponent()}
    </>
  );
};

export default ManagePolicy;